import React, { Component, ErrorInfo, ReactNode } from "react";
import styled from "styled-components";
import { Button } from "@fluentui/react-components";
import {
  ErrorCircleRegular,
  ArrowClockwiseRegular,
} from "@fluentui/react-icons";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  resetKey: number;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${({ theme }) =>
    theme.isDark ? "rgba(255, 77, 79, 0.1)" : "#fff2f0"};
  border: 2px dashed
    ${({ theme }) => (theme.isDark ? "rgba(255, 77, 79, 0.3)" : "#ffccc7")};
  border-radius: 8px;
  min-height: 300px;
  text-align: center;
`;

const ErrorIcon = styled(ErrorCircleRegular)`
  font-size: 64px;
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#a8071a")};
`;

const ErrorMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: ${({ theme }) => (theme.isDark ? "#bfbfbf" : "#595959")};
  max-width: 600px;
`;

const ErrorDetails = styled.details`
  margin-top: 16px;
  padding: 16px;
  background: ${({ theme }) => (theme.isDark ? "#1f1f1f" : "#fafafa")};
  border-radius: 4px;
  max-width: 800px;
  text-align: left;

  summary {
    cursor: pointer;
    font-weight: 600;
    margin-bottom: 8px;
    user-select: none;
  }

  pre {
    margin: 8px 0 0 0;
    padding: 12px;
    background: ${({ theme }) => (theme.isDark ? "#141414" : "#fff")};
    border: 1px solid ${({ theme }) => (theme.isDark ? "#333" : "#d9d9d9")};
    border-radius: 4px;
    overflow-x: auto;
    font-size: 12px;
    font-family: "Courier New", Courier, monospace;
    white-space: pre-wrap;
    word-break: break-all;
  }
`;

/**
 * Error Boundary component for catching and displaying errors in quote displays
 *
 * Prevents one insurer's error from crashing the entire quote display.
 * Shows a user-friendly error message with retry functionality.
 *
 * @example
 * ```tsx
 * <QuoteErrorBoundary fallbackTitle="MTPL Quotes Error">
 *   <MtplQuotesDisplay quotes={quotes} />
 * </QuoteErrorBoundary>
 * ```
 */
export class QuoteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, resetKey: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console for debugging
    console.error("QuoteErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      resetKey: prevState.resetKey + 1, // Increment key to force remount
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const {
        fallbackTitle = "Quote Display Error",
        fallbackMessage = "An error occurred while displaying the quotes. This could be due to invalid data or a temporary issue.",
      } = this.props;

      return (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorTitle>{fallbackTitle}</ErrorTitle>
          <ErrorMessage>{fallbackMessage}</ErrorMessage>
          <Button
            appearance="primary"
            icon={<ArrowClockwiseRegular />}
            onClick={this.handleReset}
          >
            Try Again
          </Button>

          {this.state.error && (
            <ErrorDetails>
              <summary>Technical Details</summary>
              <div>
                <strong>Error:</strong> {this.state.error.toString()}
              </div>
              {this.state.errorInfo && (
                <pre>{this.state.errorInfo.componentStack}</pre>
              )}
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    // Use resetKey to force remount of children when reset is clicked
    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}

export default QuoteErrorBoundary;
